
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Product } from '@/types/portfolio';
import { CheckCircle, Building, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { mockChemicalSafetyData } from '@/data/mockProducts';

interface DirectoryProductCardProps {
  product: Product;
}

export const DirectoryProductCard = ({ product }: DirectoryProductCardProps) => {
  // Get chemical safety data for this product, fallback to moderate concern
  const safetyData = mockChemicalSafetyData[product.id as keyof typeof mockChemicalSafetyData] || {
    concernLevel: 'moderate' as const,
    warningIcons: ['skin', 'environment'] as const
  };

  const getConcernColor = (concern: string) => {
    switch (concern) {
      case 'low': return 'text-green-600';
      case 'moderate': return 'text-amber-600';
      case 'high': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getWarningIcon = (type: string) => {
    const iconClasses = "px-1.5 py-0.5 rounded text-xs font-bold text-white";
    switch (type) {
      case 'carcinogen':
        return <div className={`${iconClasses} bg-red-600`} title="May cause cancer">CANCER</div>;
      case 'reproductive':
        return <div className={`${iconClasses} bg-amber-800`} title="May harm fertility or unborn child">REPRO</div>;
      case 'environment':
        return <div className={`${iconClasses} bg-green-600`} title="Harmful to aquatic life">AQUATIC</div>;
      case 'skin':
        return <div className={`${iconClasses} bg-orange-600`} title="May cause skin irritation">SKIN</div>;
      default:
        return null;
    }
  };

  return (
    <Link to={`/directory/products/${product.id}`}>
      <Card className="hover:shadow-lg transition-shadow cursor-pointer">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <CardTitle className="text-lg">{product.name}</CardTitle>
              {product.manufacturer && (
                <CardDescription className="flex items-center gap-1 mt-1">
                  <Building className="h-3 w-3" />
                  {product.manufacturer}
                </CardDescription>
              )}
            </div>
            <Badge variant="default" className="bg-green-100 text-green-800">
              <CheckCircle className="h-3 w-3 mr-1" />
              Certified
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {product.description && (
            <p className="text-sm text-muted-foreground mb-3 line-clamp-3">
              {product.description}
            </p>
          )}
          
          <div className="flex items-center gap-2 mb-3">
            <Badge variant="outline">
              {product.product_type?.replace('_', ' ') || 'Product'}
            </Badge>
          </div>

          {/* Chemical Safety Info */}
          <div className="border-t pt-3">
            <div className="flex items-center gap-1 mb-2">
              <AlertTriangle className="h-3 w-3 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Chemical Safety:</span>
              <span className={`text-xs font-semibold ${getConcernColor(safetyData.concernLevel)}`}>
                {safetyData.concernLevel.toUpperCase()} CONCERN
              </span>
            </div>
            {safetyData.warningIcons.length > 0 && (
              <div className="flex gap-1 flex-wrap">
                {safetyData.warningIcons.map((icon, index) => (
                  <div key={index}>
                    {getWarningIcon(icon)}
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};
